// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCollateralStateV2 } from '@hooks/queries/collateralv2/collateral/useCollateralStateV2';
import { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CollateralState } from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/State/module';
import { ContractId } from '@daml/types';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { MoreActionsMenu } from '../../../components/MoreActionsMenu';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import { fmt } from '../../../utils/common';
import { PageWrapper } from '../../PageWrapper';
import { useGetCollateralAgreement } from '../common/useGetCollateralAgreement';
import { Positions } from '../obligations/Positions';
import { CreateActionsCallback } from '../types';
import { CallIndependentMarginDialog } from './dialog/callIndependentMargin/CallIndependentMarginDialog';
import { CallInitialMarginDialog } from './dialog/callInitialMargin/CallInitialMarginDialog';
import { CallVariationMarginDialog } from './dialog/callVariationMargin/CallVariationMarginDialog';
import { CollateralStateRow } from './types';
import { useCollateralActions } from './useCollateralActions';

const createHeaders = (
  createActions: CreateActionsCallback<CollateralStateRow>,
  setSelectedRow: (r: CollateralStateRow) => void,
): GridColDef<CollateralStateRow>[] => {
  const renderActionCell = (params: GridRenderCellParams<CollateralStateRow>) => {
    const actions = createActions(params.row);

    if (actions.length === 0) {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        {actions.map(({ name, action, buttonProps, inMoreMenu }) =>
          inMoreMenu ? null : (
            <AsyncButton
              variant={buttonProps?.variant}
              color={buttonProps?.color}
              key={name}
              sx={{ mr: 1 }}
              onClick={async () => action(params.row)}
            >
              {name}
            </AsyncButton>
          ),
        )}
        <MoreActionsMenu
          setSelectedRow={setSelectedRow}
          row={params.row}
          actions={actions.filter((a) => a.inMoreMenu === true)}
        />
      </Box>
    );
  };

  return [
    {
      field: 'id',
      headerName: 'Agreement Id',
      valueGetter: (params) => params.row.payload.id,
      flex: 0.5,
      filterable: true,
    },
    {
      field: 'partyA',
      headerName: 'Party A',
      renderCell: (params) => <PartyDisplay party={params.row.payload.partyA} />,
      flex: 1,
      filterable: true,
      valueGetter: (params) => params.row.payload.partyA,
    },
    {
      field: 'partyB',
      headerName: 'Party B',
      renderCell: (params) => <PartyDisplay party={params.row.payload.partyB} />,
      flex: 1,
      filterable: true,
      valueGetter: (params) => params.row.payload.partyB,
    },
    {
      field: 'totalBalance',
      headerName: 'Total Balance',
      valueGetter: (params) => {
        return params.row.payload.collateralPositions ? (
          <Positions positions={params.row.payload.collateralPositions} />
        ) : null;
      },
      flex: 1,
      filterable: false,
    },
    {
      field: 'currentExposure',
      headerName: 'Current Exposure',
      valueGetter: (params) => {
        return fmt(params.row.payload.currentExposure, 2.0);
      },
      flex: 1,
      filterable: false,
    },
    {
      field: 'totalCollateralValue',
      headerName: 'Total Collateral Value',
      valueGetter: (params) => {
        return fmt(params.row.payload.totalCollateralValue, 2.0);
      },
      flex: 1,
      filterable: false,
    },
    {
      field: ' ',
      sortable: false,
      align: 'right',
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => renderActionCell(params),
      filterable: false,
    },
  ];
};

export const Collateral = () => {
  const { getCollateralAgreement } = useGetCollateralAgreement();
  const collateralStates = useCollateralStateV2();
  const { dialogName, closeDialog, createCollateralActions } = useCollateralActions();

  const rows: CollateralStateRow[] = collateralStates.data ?? [];

  const [selectedRow, setRow] = useState<CollateralStateRow | undefined>();
  const setSelectedRow = (row: CollateralStateRow) => {
    setRow((p) => {
      if (p?.contractId !== row.contractId) {
        return row;
      }
      return p;
    });
  };

  const activeDialog = useMemo(() => {
    if (!selectedRow) {
      console.warn('Tried to open dialog but there is no selected row.');
      return null;
    }

    const collateralAgreement = getCollateralAgreement(selectedRow.payload);

    if (!collateralAgreement) {
      console.warn('Could not find collateral agreement for selected row:', selectedRow);
      return null;
    }

    if (dialogName === 'callInitialMargin') {
      return (
        <CallInitialMarginDialog
          collateralAgreementCId={collateralAgreement.contractId}
          open
          onClose={closeDialog}
        />
      );
    }

    if (dialogName === 'callVariationMargin') {
      return (
        <CallVariationMarginDialog
          collateralAgreementCId={collateralAgreement.contractId}
          collateralStateCId={selectedRow.contractId as ContractId<CollateralState>}
          open
          onClose={closeDialog}
        />
      );
    }

    if (dialogName === 'callIndependentMargin') {
      return (
        <CallIndependentMarginDialog
          collateralAgreementCId={collateralAgreement.contractId}
          open
          onClose={closeDialog}
        />
      );
    }

    return null;
  }, [getCollateralAgreement, selectedRow, dialogName, closeDialog]);

  return (
    <PageWrapper pageName="Collateral">
      {activeDialog}
      <DataTable
        columns={createHeaders(createCollateralActions, setSelectedRow)}
        rows={rows}
        rowKey={(a) => a.contractId}
      />
    </PageWrapper>
  );
};
